import { type ReactNode, useCallback, useEffect } from 'react';

import type { ApiSavedSearchId } from '#/api/types/ApiTypedId';
import type {
  ApiSavedSearchUnsaved,
  UserDashboardApiSavedSearch,
} from '#/modules/userDashboard/api/savedSearch/types/UserDashboardApiSavedSearch';
import { userDashboardApiCreateSavedSearch } from '#/modules/userDashboard/api/savedSearch/userDashboardApiCreateSavedSearch';
import { userDashboardApiClientFetchAllSavedSearches } from '#/modules/userDashboard/api/savedSearch/userDashboardApiFetchAllSavedSearches';
import { userDashboardApiUpdateSavedSearch } from '#/modules/userDashboard/api/savedSearch/userDashboardApiUpdateSavedSearch';
import { trackEvent } from '#/utils/analytics/track/trackEvent';
import { pushFlashMessage } from '#/zustand-stores/flashMessagesStore';
import { createZustandContextStore } from '#/zustand-stores/utils/createZustandContextStore';

type SavedSearchesContextState = {
  savedSearches: UserDashboardApiSavedSearch[] | null;
  recentlySavedSearchId: ApiSavedSearchId | null;
};

const {
  ZustandContextProvider,
  useZustandContextStore,
  useZustandContextSelector,
} = createZustandContextStore<SavedSearchesContextState>({
  fallbackState: { savedSearches: null, recentlySavedSearchId: null },
});

export function SavedSearchesContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <ZustandContextProvider
      initialState={{ savedSearches: null, recentlySavedSearchId: null }}
    >
      {children}
    </ZustandContextProvider>
  );
}

// Hooks

let savedSearchesPromise: Promise<UserDashboardApiSavedSearch[]> | null = null;

function fetchSavedSearchesOnce(
  store: ReturnType<typeof useZustandContextStore>,
) {
  if (!savedSearchesPromise) {
    savedSearchesPromise = userDashboardApiClientFetchAllSavedSearches();

    void savedSearchesPromise.then((savedSearches) => {
      store.setState({ savedSearches });
    });
  }

  return savedSearchesPromise;
}

export function useSavedSearches() {
  const savedSearches = useZustandContextSelector(
    (state) => state.savedSearches,
  );

  const store = useZustandContextStore();
  useEffect(() => {
    void fetchSavedSearchesOnce(store);
  }, [store]);

  return { savedSearches };
}

export function useSavedSearchesActions() {
  const store = useZustandContextStore();

  const createSavedSearch = useCallback(
    async (unsavedSearch: ApiSavedSearchUnsaved): Promise<void> => {
      const newSavedSearch =
        await userDashboardApiCreateSavedSearch(unsavedSearch);

      const currentSavedSearches = await fetchSavedSearchesOnce(store);

      store.setState({
        savedSearches: [...currentSavedSearches, newSavedSearch],
        recentlySavedSearchId: newSavedSearch.id,
      });

      pushFlashMessage({ type: 'SAVED_SEARCH' });
      trackEvent('Created Email Alert');
    },
    [store],
  );

  const updateSavedSearch = useCallback(
    async (
      id: ApiSavedSearchId,
      values: { name: string; notifyByEmail: boolean },
    ) => {
      const updatedSavedSearch = await userDashboardApiUpdateSavedSearch(
        id,
        values,
      );
      store.setState((currentState) => ({
        savedSearches: currentState.savedSearches?.map((savedSearch) =>
          savedSearch.id === id ? updatedSavedSearch : savedSearch,
        ),
      }));
    },
    [store],
  );

  const dismissRecentlySavedSearch = useCallback(() => {
    store.setState({ recentlySavedSearchId: null });
  }, [store]);

  return {
    createSavedSearch,
    updateSavedSearch,
    dismissRecentlySavedSearch,
  };
}

export function useRecentlySavedSearchId() {
  const recentlySavedSearchId = useZustandContextSelector(
    (state) => state.recentlySavedSearchId,
  );

  return { recentlySavedSearchId };
}
